import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ConfigureRooms = () => {
  const navigate = useNavigate();
  const { propertyId, blockId, floorId } = useParams();
  
  // Get provider information from localStorage
  const providerType = localStorage.getItem('provider_type');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  const [property, setProperty] = useState(null);
  const [block, setBlock] = useState(null);
  const [floor, setFloor] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [debugModal, setDebugModal] = useState(false);
  const [debugRoom, setDebugRoom] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (property && block && floor) {
      return `Configure Rooms - Floor ${block.block_number}${floor.floor_number}`;
    }
    return 'Configure Rooms';
  };

  const getSubtitleMessage = () => {
    if (property) {
      return property.trading_name || `Property #${property.id}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  // Fetch property, block, floor, and rooms data
  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId || !blockId || !floorId || regStatus !== '2') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        if (propertyResponse.status === 200) {
          setProperty(propertyResponse.data);
          
          // Fetch room types for this property
          try {
            const roomTypesResponse = await axiosInstance.get(`/api/room-types/?property=${propertyId}`);
            if (roomTypesResponse.status === 200) {
              setRoomTypes(roomTypesResponse.data);
            }
          } catch (rtError) {
            console.error('Error fetching room types:', rtError);
            setRoomTypes([]);
          }
        }

        // Fetch block details
        const blockResponse = await axiosInstance.get(`/api/blocks/${blockId}/`);
        if (blockResponse.status === 200) {
          setBlock(blockResponse.data);
        }

        // Fetch floor details
        const floorResponse = await axiosInstance.get(`/api/floors/${floorId}/`);
        if (floorResponse.status === 200) {
          const floorData = floorResponse.data;
          setFloor(floorData);
          
          // Check if floor is configured
          if (!floorData.is_configured) {
            setAlert({
              type: 'warning',
              message: 'This floor needs to be configured first before you can view rooms.',
              visible: true
            });
            setLoading(false);
            return;
          }
          
          // Fetch rooms from API
          try {
            const roomsResponse = await axiosInstance.get(`/api/floors/${floorId}/rooms/`);
            if (roomsResponse.status === 200) {
              setRooms(roomsResponse.data);
            }
          } catch (roomError) {
            console.error('Error fetching rooms:', roomError);
            setRooms([]);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load rooms. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId, blockId, floorId, regStatus]);

  const handleBackToFloors = () => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/block/${blockId}/configure-floors`);
  };

  const handleConfigureFloor = () => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/block/${blockId}/configure-floors/${floorId}/maintain`);
  };

  const handleViewRoom = (roomId) => {
    // TODO: Implement view room functionality
    console.log(`View room ${roomId}`);
  };

  const handleMaintainRoom = (roomId) => {
    navigate(`/provider-dashboard/manage-room/${roomId}`);
  };

  const handleDebugRoom = async (roomId) => {
    setDebugLoading(true);
    setDebugModal(true);
    try {
      // Fetch detailed room information
      const roomResponse = await axiosInstance.get(`/api/rooms/${roomId}/`);
      const roomData = roomResponse.data;

      // Fetch property campus links to see which campuses this property serves
      const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
      const campusLinksResponse = await axiosInstance.get(`/api/properties/${propertyId}/campus-links/`);

      setDebugRoom({
        ...roomData,
        property: propertyResponse.data,
        campusLinks: campusLinksResponse.data
      });
    } catch (error) {
      console.error('Error fetching room debug info:', error);
      setDebugRoom({ error: 'Failed to load room information' });
    } finally {
      setDebugLoading(false);
    }
  };

  const toggleDebugModal = () => {
    setDebugModal(!debugModal);
    if (debugModal) {
      setDebugRoom(null);
    }
  };

  const handleUnlockRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to unlock this room? This will delete ALL bookings for this room. This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/rooms/${roomId}/unlock/`);
      
      if (response.data.success) {
        setAlert({
          type: 'success',
          message: `Room unlocked successfully! Deleted ${response.data.bookings_deleted} booking(s).`,
          visible: true
        });
        
        // Auto-hide success message
        setTimeout(() => {
          setAlert({ type: '', message: '', visible: false });
        }, 5000);
        
        // Refresh the room data
        await handleDebugRoom(roomId);
        
        // Refresh the rooms list
        try {
          const roomsResponse = await axiosInstance.get(`/api/floors/${floorId}/rooms/`);
          if (roomsResponse.status === 200) {
            setRooms(roomsResponse.data);
          }
        } catch (roomError) {
          console.error('Error refreshing rooms:', roomError);
        }
      } else {
        setAlert({
          type: 'danger',
          message: `Failed to unlock room: ${response.data.error}`,
          visible: true
        });
      }
    } catch (error) {
      console.error('Error unlocking room:', error);
      setAlert({
        type: 'danger',
        message: `Error unlocking room: ${error.response?.data?.error || error.message}`,
        visible: true
      });
    }
  };

  const handleRoomTypeChange = async (roomId, roomTypeId) => {
    try {
      // Update room with selected room type
      const response = await axiosInstance.patch(`/api/rooms/${roomId}/`, {
        room_type: roomTypeId ? parseInt(roomTypeId) : null
      });
      
      if (response.status === 200) {
        // Update local state
        setRooms(prevRooms => 
          prevRooms.map(room => 
            room.id === roomId 
              ? { ...room, room_type: roomTypeId ? parseInt(roomTypeId) : null }
              : room
          )
        );
        
        setAlert({
          type: 'success',
          message: 'Room type updated successfully',
          visible: true
        });
        
        // Auto-hide success message
        setTimeout(() => {
          setAlert({ type: '', message: '', visible: false });
        }, 3000);
      }
    } catch (error) {
      console.error('Error updating room type:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to update room type. Please try again.',
        visible: true
      });
    }
  };

  const handleBulkRoomTypeChange = async (selectedRoomTypeId) => {
    if (!selectedRoomTypeId) {
      setAlert({
        type: 'warning',
        message: 'Please select a room type for bulk update.',
        visible: true
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to update all ${rooms.length} rooms on this floor to the selected room type?`)) {
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      // Update each room
      for (const room of rooms) {
        try {
          await axiosInstance.patch(`/api/rooms/${room.id}/`, {
            room_type: parseInt(selectedRoomTypeId)
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to update room ${room.id}:`, error);
          failCount++;
        }
      }

      // Update local state for all successfully updated rooms
      setRooms(prevRooms => 
        prevRooms.map(room => ({
          ...room,
          room_type: parseInt(selectedRoomTypeId)
        }))
      );

      // Show result message
      if (failCount === 0) {
        setAlert({
          type: 'success',
          message: `Successfully updated all ${successCount} rooms to the selected room type.`,
          visible: true
        });
      } else {
        setAlert({
          type: 'warning',
          message: `Updated ${successCount} rooms successfully. ${failCount} rooms failed to update.`,
          visible: true
        });
      }

      // Auto-hide message
      setTimeout(() => {
        setAlert({ type: '', message: '', visible: false });
      }, 5000);

    } catch (error) {
      console.error('Error during bulk update:', error);
      setAlert({
        type: 'danger',
        message: 'Bulk update failed. Please try again.',
        visible: true
      });
    }
  };

  // Format room identifier as BBFF-R (e.g., 0304-2 for Block 03, Floor 04, Room 2)
  const formatRoomIdentifier = (room) => {
    if (block && floor) {
      return `${block.block_number}${floor.floor_number}-${room.room_number}`;
    }
    return room.room_number;
  };

  // If floor is not configured, show call to action
  if (!loading && floor && !floor.is_configured) {
    return (
      <div className="container-fluid py-4">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h2 className="mb-1">{getWelcomeMessage()}</h2>
                {getSubtitleMessage() && (
                  <p className="text-muted mb-1">{getSubtitleMessage()}</p>
                )}
                <Badge color="info" className="me-2">
                  {getProviderTypeDisplay()}
                </Badge>
                {block && floor && (
                  <Badge color="secondary" className="me-2">
                    Floor {block.block_number}{floor.floor_number}
                  </Badge>
                )}
                {property && property.campus_links && property.campus_links.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted d-block mb-1"><strong>Serving:</strong></small>
                    {property.campus_links.map((link, idx) => (
                      <Badge key={idx} color="success" className="me-1 mb-1">
                        {link.institution_name} (ID: {link.institution_id}) - {link.campus_name} (ID: {link.campus_id}) | {link.beds_reserved} beds
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                color="outline-secondary" 
                onClick={handleBackToFloors}
                className="btn-sm"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to Floors
              </Button>
            </div>
          </div>
        </div>

        {/* Alert */}
        {alert.visible && (
          <Row className="mb-3">
            <Col>
              <Alert 
                color={alert.type}
                toggle={() => setAlert({ ...alert, visible: false })}
              >
                {alert.message}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Call to Action Card */}
        <Row>
          <Col md="12">
            <Card className="border-warning">
              <CardBody className="text-center py-5">
                <i className="bi bi-exclamation-triangle display-1 text-warning mb-4"></i>
                <h3 className="mb-3">Floor Not Configured</h3>
                <p className="text-muted mb-4">
                  Before you can view rooms, you need to configure this floor first.<br/>
                  Please provide the floor details including gender designation, number of single rooms, and number of shared rooms.
                </p>
                <Button 
                  color="primary" 
                  size="lg"
                  onClick={handleConfigureFloor}
                >
                  <i className="bi bi-gear me-2"></i>
                  Configure Floor Now
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              {block && floor && (
                <Badge color="secondary" className="me-2">
                  Block {block.block_number} - Floor {floor.floor_number}
                </Badge>
              )}
              {property && property.campus_links && property.campus_links.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted d-block mb-1"><strong>Serving:</strong></small>
                  {property.campus_links.map((link, idx) => (
                    <Badge key={idx} color="success" className="me-1 mb-1">
                      {link.institution_name} (ID: {link.institution_id}) - {link.campus_name} (ID: {link.campus_id}) | {link.beds_reserved} beds
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToFloors}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Floors
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Bulk Update Section */}
      {!loading && rooms.length > 0 && (
        <Row className="mb-3">
          <Col md="12">
            <Card className="bg-light">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <i className="bi bi-lightning-charge-fill text-warning" style={{ fontSize: '1.5rem' }}></i>
                  </div>
                  <div className="flex-grow-1 me-3">
                    <h6 className="mb-1">Bulk Update Room Type</h6>
                    <small className="text-muted">
                      Update all {rooms.length} rooms on this floor to the same room type
                    </small>
                  </div>
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select me-2"
                      style={{ width: '250px' }}
                      onChange={(e) => handleBulkRoomTypeChange(e.target.value)}
                      defaultValue=""
                    >
                      <option value="">Select Room Type to Apply</option>
                      {roomTypes.map((roomType) => (
                        <option key={roomType.id} value={roomType.id}>
                          {roomType.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Rooms Table */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-door-closed me-2 text-primary"></i>
                Floor Rooms
              </CardTitle>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading rooms...</p>
                </div>
              ) : (
                <>
                  {rooms.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-door-closed display-4 text-muted"></i>
                      <h5 className="mt-3 text-muted">No Rooms Found</h5>
                      <p className="text-muted">
                        This floor does not have any rooms yet. Rooms are automatically created when you configure the floor with room counts.
                      </p>
                      <Button 
                        color="primary"
                        onClick={handleConfigureFloor}
                      >
                        <i className="bi bi-gear me-2"></i>
                        Configure Floor
                      </Button>
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ paddingBottom: '150px' }}>
                      <Table hover>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Room</th>
                            <th>Gender</th>
                            <th>Total Beds</th>
                            <th>Status</th>
                            <th>Inspection</th>
                            <th>Room Type</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rooms.map((room) => (
                            <tr key={room.id}>
                              <td>
                                <span className="text-muted">{room.id}</span>
                              </td>
                              <td>
                                <div className="fw-bold">
                                  {formatRoomIdentifier(room)}
                                </div>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {room.room_gender ? room.room_gender.charAt(0).toUpperCase() + room.room_gender.slice(1) : 'Not Set'}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">{room.no_of_beds || 0}</span>
                              </td>
                              <td>
                                <Badge 
                                  color={
                                    room.status === 'available' ? 'success' :
                                    room.status === 'partially_occupied' ? 'warning' :
                                    room.status === 'fully_occupied' ? 'danger' :
                                    room.status === 'maintenance' ? 'info' :
                                    'secondary'
                                  }
                                >
                                  {room.status ? room.status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                                </Badge>
                              </td>
                              <td>
                                <Badge 
                                  color={
                                    room.inspection_status === 'inspection_passed' ? 'success' :
                                    room.inspection_status === 'inspection_pending' ? 'warning' :
                                    room.inspection_status === 'inspection_failed' ? 'danger' :
                                    room.inspection_status === 'inspection_requested' ? 'info' :
                                    'secondary'
                                  }
                                >
                                  {room.inspection_status ? room.inspection_status.replace(/_/g, ' ').toUpperCase() : 'NOT INSPECTED'}
                                </Badge>
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={room.room_type || ''}
                                  onChange={(e) => handleRoomTypeChange(room.id, e.target.value)}
                                  style={{ minWidth: '150px' }}
                                >
                                  <option value="">Set Room Type</option>
                                  {roomTypes.map((roomType) => (
                                    <option key={roomType.id} value={roomType.id}>
                                      {roomType.name}
                                      {room.room_type === roomType.id && ' ✓'}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle 
                                    color="light" 
                                    size="sm" 
                                    className="btn-outline-secondary"
                                    caret={false}
                                  >
                                    <i className="bi bi-three-dots"></i>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <DropdownItem 
                                      onClick={() => handleViewRoom(room.id)}
                                    >
                                      <i className="bi bi-eye me-2"></i>
                                      View
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleMaintainRoom(room.id)}
                                    >
                                      <i className="bi bi-gear me-2"></i>
                                      Maintain
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleDebugRoom(room.id)}
                                    >
                                      <i className="bi bi-bug me-2"></i>
                                      Debug
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Action Summary */}
      {rooms.length > 0 && (
        <Row className="mt-4">
          <Col md="12">
            <Card className="border-0 bg-light">
              <CardBody>
                <CardTitle tag="h6" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Room Management Actions
                </CardTitle>
                <Row>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>View:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>View room details and specifications</li>
                      <li>See bed availability and occupancy</li>
                      <li>Review room amenities</li>
                    </ul>
                  </Col>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>Maintain:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>Edit room information</li>
                      <li>Update room settings</li>
                      <li>Manage room status</li>
                    </ul>
                  </Col>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>Debug:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>View campus associations</li>
                      <li>Check room configuration</li>
                      <li>Review technical details</li>
                    </ul>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Debug Modal */}
      <Modal isOpen={debugModal} toggle={toggleDebugModal} size="lg">
        <ModalHeader toggle={toggleDebugModal}>
          <i className="bi bi-bug me-2"></i>
          Room Debug Information
        </ModalHeader>
        <ModalBody>
          {debugLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading room information...</p>
            </div>
          ) : debugRoom && debugRoom.error ? (
            <Alert color="danger">{debugRoom.error}</Alert>
          ) : debugRoom ? (
            <>
              {/* Basic Room Information */}
              <Card className="mb-3">
                <CardBody>
                  <CardTitle tag="h6" className="text-primary mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Basic Information
                  </CardTitle>
                  <Row>
                    <Col md="6">
                      <p><strong>Room ID:</strong> {debugRoom.id}</p>
                      <p><strong>Room Number:</strong> {debugRoom.room_number}</p>
                      <p><strong>Status:</strong> {debugRoom.status?.replace('_', ' ').toUpperCase()}</p>
                      <p><strong>Gender:</strong> {debugRoom.room_gender || 'N/A'}</p>
                    </Col>
                    <Col md="6">
                      <p><strong>Is Gender Locked:</strong> {debugRoom.is_gender_locked ? 'Yes' : 'No'}</p>
                      <p><strong>Locked Gender:</strong> {debugRoom.locked_gender?.toUpperCase() || 'N/A'}</p>
                      <p><strong>Number of Beds:</strong> {debugRoom.no_of_beds || 0}</p>
                      <p><strong>Capacity Override:</strong> {debugRoom.capacity_override || 'N/A'}</p>
                    </Col>
                  </Row>
                  {debugRoom.notes && (
                    <Row className="mt-2">
                      <Col md="12">
                        <p><strong>Notes:</strong> {debugRoom.notes}</p>
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>

              {/* Room Type & Pricing */}
              <Card className="mb-3">
                <CardBody>
                  <CardTitle tag="h6" className="text-primary mb-3">
                    <i className="bi bi-tag me-2"></i>
                    Room Type & Pricing
                  </CardTitle>
                  {debugRoom.room_type_details ? (
                    <>
                      <Row>
                        <Col md="6">
                          <p><strong>Room Type ID:</strong> {debugRoom.room_type_details.id}</p>
                          <p><strong>Name:</strong> {debugRoom.room_type_details.name}</p>
                          <p><strong>Size (sqm):</strong> {debugRoom.room_type_details.size_sqm || 'N/A'}</p>
                        </Col>
                        <Col md="6">
                          <p><strong>Semester Price:</strong> R{debugRoom.room_type_details.semester_price || '0'}</p>
                          <p><strong>Trimester Price:</strong> R{debugRoom.room_type_details.trimester_price || '0'}</p>
                          <p><strong>Annual Price:</strong> R{debugRoom.room_type_details.annual_price || '0'}</p>
                        </Col>
                      </Row>
                      {debugRoom.room_type_details.description && (
                        <Row className="mt-2">
                          <Col md="12">
                            <p><strong>Description:</strong> {debugRoom.room_type_details.description}</p>
                          </Col>
                        </Row>
                      )}
                    </>
                  ) : (
                    <p className="text-muted">No room type assigned</p>
                  )}
                </CardBody>
              </Card>

              {/* Campus/Institution Information */}
              {debugRoom.campus && (
                <Card className="mb-3">
                  <CardBody>
                    <CardTitle tag="h6" className="text-primary mb-3">
                      <i className="bi bi-building me-2"></i>
                      Campus/Institution Information
                    </CardTitle>
                    <Row>
                      <Col md="6">
                        <p><strong>Campus ID:</strong> {debugRoom.campus.id}</p>
                        <p><strong>Campus Name:</strong> {debugRoom.campus.name}</p>
                      </Col>
                      <Col md="6">
                        <p><strong>Institution ID:</strong> {debugRoom.campus.institution?.id || 'N/A'}</p>
                        <p><strong>Institution Name:</strong> {debugRoom.campus.institution?.name || 'N/A'}</p>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}

              {/* Property Campus Associations */}
              <Card className="mb-3">
                <CardBody>
                  <CardTitle tag="h6" className="text-primary mb-3">
                    <i className="bi bi-geo-alt me-2"></i>
                    Property Campus Associations
                  </CardTitle>
                  {debugRoom.campusLinks && debugRoom.campusLinks.length > 0 ? (
                    <div>
                      <p className="mb-2">This property serves {debugRoom.campusLinks.length} campus(es):</p>
                      <ul className="list-unstyled">
                        {debugRoom.campusLinks.map((link, idx) => (
                          <li key={idx} className="mb-3 ps-3 border-start border-primary border-3">
                            <p className="mb-1"><strong>• Campus:</strong> {link.campus_name || link.campus}</p>
                            <p className="mb-1 ms-3"><strong>Campus ID:</strong> {link.campus}</p>
                            <p className="mb-1 ms-3"><strong>Institution:</strong> {link.institution_name || 'N/A'}</p>
                            <p className="mb-1 ms-3"><strong>Allocated Beds:</strong> {link.beds_reserved || link.allocated_beds || 0}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-muted">No property campus associations found</p>
                  )}
                </CardBody>
              </Card>

              {/* Inspection Details */}
              <Card className="mb-3">
                <CardBody>
                  <CardTitle tag="h6" className="text-primary mb-3">
                    <i className="bi bi-clipboard-check me-2"></i>
                    Inspection Details
                  </CardTitle>
                  <Row>
                    <Col md="6">
                      <p><strong>Inspection Status:</strong> {debugRoom.inspection_status?.replace('_', ' ').toUpperCase() || 'N/A'}</p>
                      <p><strong>Inspection Date:</strong> {debugRoom.inspection_date ? new Date(debugRoom.inspection_date).toLocaleString() : 'N/A'}</p>
                    </Col>
                    <Col md="6">
                      <p><strong>Inspector:</strong> {debugRoom.inspector || 'N/A'}</p>
                    </Col>
                  </Row>
                  {debugRoom.inspection_notes && (
                    <Row className="mt-2">
                      <Col md="12">
                        <p><strong>Inspection Notes:</strong> {debugRoom.inspection_notes}</p>
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>

              {/* Room Images */}
              {debugRoom.images && debugRoom.images.length > 0 && (
                <Card className="mb-3">
                  <CardBody>
                    <CardTitle tag="h6" className="text-primary mb-3">
                      <i className="bi bi-image me-2"></i>
                      Room Images
                    </CardTitle>
                    <p className="mb-2"><strong>Total Images:</strong> {debugRoom.images.length}</p>
                    <ul className="list-unstyled">
                      {debugRoom.images.map((img, idx) => (
                        <li key={idx} className="mb-2">
                          <p className="mb-1"><strong>• Image ID:</strong> {img.id}</p>
                          <p className="mb-1 ms-3"><strong>Path:</strong> {img.image}</p>
                          {img.description && <p className="mb-1 ms-3"><strong>Description:</strong> {img.description}</p>}
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              )}

              {/* Bookings */}
              {debugRoom.bookings && debugRoom.bookings.length > 0 && (
                <Card className="mb-3">
                  <CardBody>
                    <CardTitle tag="h6" className="text-primary mb-3">
                      <i className="bi bi-calendar-check me-2"></i>
                      Bookings
                    </CardTitle>
                    <p className="mb-3"><strong>Total Bookings:</strong> {debugRoom.bookings.length}</p>
                    {debugRoom.bookings.map((booking, idx) => (
                      <Card key={idx} className="mb-3 border">
                        <CardBody className="p-3">
                          <Row>
                            <Col md="6">
                              <p className="mb-1"><strong>Booking ID:</strong> {booking.id}</p>
                              <p className="mb-1"><strong>Student:</strong> {booking.student_first_name && booking.student_last_name ? `${booking.student_first_name} ${booking.student_last_name}` : booking.student_email || 'N/A'}</p>
                              <p className="mb-1"><strong>Email:</strong> {booking.student_email || 'N/A'}</p>
                              <p className="mb-1"><strong>Gender:</strong> {booking.student_gender?.toUpperCase() || 'N/A'}</p>
                              <p className="mb-1"><strong>Bed:</strong> {booking.bed_number || 'N/A'}</p>
                            </Col>
                            <Col md="6">
                              <p className="mb-1"><strong>Check-in:</strong> {booking.check_in_date ? new Date(booking.check_in_date).toLocaleDateString() : 'N/A'}</p>
                              <p className="mb-1"><strong>Check-out:</strong> {booking.check_out_date ? new Date(booking.check_out_date).toLocaleDateString() : 'N/A'}</p>
                              <p className="mb-1"><strong>Status:</strong> <Badge color={booking.status === 'confirmed' || booking.status === 'checked_in' ? 'success' : booking.status === 'cancelled' ? 'danger' : 'warning'}>{booking.status?.replace('_', ' ').toUpperCase()}</Badge></p>
                              <p className="mb-1"><strong>Total Price:</strong> R{booking.total_price || '0'}</p>
                              <p className="mb-1"><strong>Paid:</strong> {booking.is_paid ? 'Yes' : 'No'}</p>
                              <p className="mb-1"><strong>Created:</strong> {booking.created_at ? new Date(booking.created_at).toLocaleString() : 'N/A'}</p>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </CardBody>
                </Card>
              )}

              {/* Timestamps */}
              <Card>
                <CardBody>
                  <CardTitle tag="h6" className="text-primary mb-3">
                    <i className="bi bi-clock me-2"></i>
                    Timestamps
                  </CardTitle>
                  <Row>
                    <Col md="6">
                      <p><strong>Created:</strong> {debugRoom.created_at ? new Date(debugRoom.created_at).toLocaleString() : 'N/A'}</p>
                    </Col>
                    <Col md="6">
                      <p><strong>Updated:</strong> {debugRoom.updated_at ? new Date(debugRoom.updated_at).toLocaleString() : 'N/A'}</p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </>
          ) : (
            <p className="text-muted">No room data available</p>
          )}
        </ModalBody>
        <ModalFooter>
          {debugRoom && !debugRoom.error && debugRoom.room_gender === 'mixed' && debugRoom.is_gender_locked && (
            <Button 
              color="warning" 
              onClick={() => handleUnlockRoom(debugRoom.id)}
              className="me-auto"
            >
              <i className="bi bi-unlock me-2"></i>
              Unlock Room & Delete Bookings
            </Button>
          )}
          <Button color="secondary" onClick={toggleDebugModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ConfigureRooms;
